// Thin wrapper around authenticatedFetch. Mirrors CategorizeAndClean/apiClient.js.
import { authenticatedFetch } from '../AuthComponent';

const BASE = '/api/offboarding';

async function jsonOrThrow(resp) {
  if (!resp.ok) {
    let detail = '';
    try {
      const raw = await resp.text();
      try {
        const j = JSON.parse(raw);
        detail = typeof j === 'string' ? j : j.detail ? String(j.detail) : raw;
      } catch {
        detail = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240);
      }
    } catch { /* status alone is enough */ }
    throw new Error(`${resp.status} ${resp.statusText}${detail ? `: ${detail}` : ''}`);
  }
  return resp.json();
}

export const api = {
  preview: (rows) =>
    authenticatedFetch(`${BASE}/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(rows),
    }).then(jsonOrThrow),

  csvPreview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return authenticatedFetch(`${BASE}/csv-preview`, { method: 'POST', body: formData }).then(jsonOrThrow);
  },

  // CSV bulk import's multi-system entrypoint (added 2026-09-23) — every
  // registered connector runs for every row, not just Workspace/Phase 1.
  // rows: [{email, full_name, forward_to}, ...] — email/forward_to may be
  // blank (more than half of Soccer Post's staff have no company email
  // at all), full_name is required. One synchronous POST, no SSE — see
  // offboarding/full_run.py's run_full_batch().
  runFullCsvBatch: (rows, confirmPhrase) =>
    authenticatedFetch(`${BASE}/full-run/csv-batch`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rows, confirm_phrase: confirmPhrase }),
    }).then(jsonOrThrow),

  history: () => authenticatedFetch(`${BASE}/history`).then(jsonOrThrow),

  // Multi-system offboarding runs (Workspace + Shopify/Lightspeed/Team
  // Portal/...). See offboarding/full_run.py for the run/account/action
  // model these map onto.
  // scheduledForIso: optional ISO datetime string. Omit/null to run now.
  startFullRun: (fullName, email, forwardTo, confirmPhrase, scheduledForIso, notifyEmail) =>
    authenticatedFetch(`${BASE}/full-run`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        full_name: fullName, email, forward_to: forwardTo, confirm_phrase: confirmPhrase,
        scheduled_for: scheduledForIso || null, notify_email: notifyEmail || null,
      }),
    }).then(jsonOrThrow),

  getFullRun: (runId) => authenticatedFetch(`${BASE}/full-run/${runId}`).then(jsonOrThrow),

  cancelFullRun: (runId) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/cancel`, { method: 'POST' }).then(jsonOrThrow),

  fullRunHistory: (search, needsAttention) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (needsAttention) params.set('needs_attention', 'true');
    const qs = params.toString();
    return authenticatedFetch(`${BASE}/full-run/history${qs ? `?${qs}` : ''}`).then(jsonOrThrow);
  },

  reportWorkspaceComplete: (runId, exitCode, detail) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/workspace-complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exit_code: exitCode, detail: detail || '' }),
    }).then(jsonOrThrow),

  resolveFullRunAction: (runId, actionId, decision, candidateId) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/actions/${actionId}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, candidate_id: candidateId || null }),
    }).then(jsonOrThrow),

  retryFullRunAction: (runId, actionId) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/actions/${actionId}/retry`, { method: 'POST' }).then(jsonOrThrow),

  markFullRunActionDone: (runId, actionId) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/actions/${actionId}/mark-done`, { method: 'POST' }).then(jsonOrThrow),

  retryFailedFullRun: (runId) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/retry-failed`, { method: 'POST' }).then(jsonOrThrow),

  changeFullRunForwarding: (runId, recipients) =>
    authenticatedFetch(`${BASE}/full-run/${runId}/forwarding`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recipients }),
    }).then(jsonOrThrow),

  deletionCandidates: () => authenticatedFetch(`${BASE}/deletion/candidates`).then(jsonOrThrow),

  startTransfer: (archivedEmail) =>
    authenticatedFetch(`${BASE}/deletion/${encodeURIComponent(archivedEmail)}/start-transfer`, {
      method: 'POST',
    }).then(jsonOrThrow),

  transferStatus: (archivedEmail) =>
    authenticatedFetch(`${BASE}/deletion/${encodeURIComponent(archivedEmail)}/transfer-status`).then(jsonOrThrow),

  deleteAccount: (archivedEmail, confirmPhrase) =>
    authenticatedFetch(`${BASE}/deletion/${encodeURIComponent(archivedEmail)}/delete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm_phrase: confirmPhrase }),
    }).then(jsonOrThrow),

  // SSE: returns an AbortController-like { abort() }. onEvent gets parsed dicts.
  run(rows, confirmPhrase, onEvent, onDone) {
    const ac = new AbortController();
    const token = localStorage.getItem('access_token');
    fetch(`${BASE}/run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ rows, confirm_phrase: confirmPhrase }),
      signal: ac.signal,
    })
      .then(async (resp) => {
        if (!resp.ok) {
          const raw = await resp.text().catch(() => '');
          let message;
          try {
            message = JSON.parse(raw).detail || raw;
          } catch {
            message = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 240);
          }
          onEvent({ line: `ERROR: ${resp.status} ${resp.statusText}${message ? ` — ${message}` : ''}` });
          onDone({ exit_code: 1 });
          return;
        }
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const parsed = JSON.parse(line.slice(6));
                if (parsed.done) onDone(parsed);
                else onEvent(parsed);
              } catch { /* ignore malformed SSE line */ }
            }
          }
        }
      })
      .catch((e) => {
        if (e?.name !== 'AbortError') {
          onEvent({ line: `Network error: ${e?.message || e}` });
          onDone({ exit_code: 1 });
        }
      });
    return ac;
  },
};
